import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {EnabledStatus, ICollectionItem, ICreateOrUpdateCollection, ICreateOrUpdateItem, ListResponse,} from '@/models';
import {changeEnabledStatus, http, updateItemById} from '@/utils';

import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {handleApiCallInStore} from './utils';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';

export interface ICollectionStore {
  collections: ICollectionItem[];
  collectionDetails: ICollectionItem;
  nbHits: number;
  getCollectionsList: (
    params,
    onSuccess?: (items: ICollectionItem[]) => void
  ) => void;
  getCollectionById: (
    collectionId: string,
    onSuccess: (items: ICollectionItem) => void
  ) => void;
  createOrUpdateCollection: (
    data: ICreateOrUpdateItem<ICreateOrUpdateCollection>
  ) => void;
  changeCollectionStatus: (payload: EnabledStatus) => void;
  sortCollections: (
    payload: { collections: string[] },
    onSuccess: () => void
  ) => void;
}

export const useCollectionStore = create(
  persist<ICollectionStore>(
    set => ({
      collectionDetails: undefined,
      collections: [],
      nbHits: 0,
      sortCollections: (payload, onSuccess) => {
        handleApiCallInStore(LoadingKeys.SortCollections, async () => {
          await http.put<{ collections: string[] }>(
            API_ENDPOINTS.collection.sort,
            payload
          );
          await onSuccess();
        });
      },
      createOrUpdateCollection: ({id, onSuccess, payload}) => {
        handleApiCallInStore(LoadingKeys.CreateOrUpdateCollection, async () => {
          if (id) {
            await http.put(
              API_ENDPOINTS.collection.createOrUpdate(id),
              payload
            );
          } else {
            await http.post(
              API_ENDPOINTS.collection.createOrUpdate(id),
              payload
            );
          }

          await onSuccess();
        });
      },
      getCollectionsList: async (params, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetAllCollections, async () => {
          const {data} = await http.get<ListResponse<ICollectionItem>>(
            API_ENDPOINTS.collection.all,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({
            collections: items ?? [],
            nbHits: nbHits ?? 0,
          }));
          if (onSuccess) {
            onSuccess(items);
          }
        });
      },
      getCollectionById: async (collectionId, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetCollectionById, async () => {
          const {data} = await http.get<ICollectionItem>(
            API_ENDPOINTS.collection.byId(collectionId)
          );
          set(() => ({collectionDetails: data}));
          await onSuccess(data);
        });
      },
      changeCollectionStatus: async payload => {
        handleApiCallInStore(LoadingKeys.changeCollectionStatus, async () => {
          const {isEnabled, id} = payload;
          await http.put(API_ENDPOINTS.collection.changeStatus(id), {
            isEnabled,
          });
          set(({collections}) => ({
            collections: updateItemById(
              collections,
              'isEnabled',
              isEnabled,
              id
            ),
          }));
          changeEnabledStatus(isEnabled, t('collection'));
        });
      },
    }),
    {
      name: 'Collection-storage',

    }
  )
);
